import { AppState, Auth0Provider } from '@auth0/auth0-react'
import { ChakraProvider } from '@chakra-ui/react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import Layout from '../components/layout'
import AuthorizedApolloProvider from '@/hooks/authorized_apollo'
import { IDTokenClaimsProvider } from '@/hooks/id_token_claims'
import { InitializeAtoms } from '@/hooks/initialize_atoms'
import { ReportTableFilterProvider } from '@/hooks/report_table_filters'
import { SpoofReportTableFilterProvider } from '@/hooks/spoof_report_table_filters'
import theme from '@/theme'
import './select_table.css'

import { Provider as JotaiProvider } from 'jotai'
import { SlackNotificationUserOpenedHook } from '@/hooks/slack_notifications'
import { ReportsProvider } from './reports'
import { StatsContextProvider } from './stats'
import { EntitiesTableProvider } from '@/hooks/entities_table_filters'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { ErrorBoundary } from '@/components/error_boundary'
import ToSWrapper from '@/components/tos_wrapper'
import Cookies from 'js-cookie'
import { AppSettingsDefaultLandingPageToRoute } from '../middleware-constants'

if (typeof window !== 'undefined') {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: `/ingest`,
    ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    loaded: (ph) => {
      if (process.env.NODE_ENV === 'development') ph.debug()

      ph.onFeatureFlags(() => {
        const isEnabled = ph.isFeatureEnabled('click-tracking')

        if (!isEnabled) {
          ph.opt_out_capturing()
        } else {
          ph.opt_in_capturing()
        }
      })
    },
  })
}

function MyApp({ Component, pageProps }) {
  const router = useRouter()

  const layout = Component.getLayout || ((page) => <Layout>{page}</Layout>)

  return (
    <>
      <Head>
        <meta content="width=device-width, initial-scale=1" name="viewport" />

        <link href="/apple-touch-icon.png" rel="apple-touch-icon" sizes="180x180" />

        <link href="/favicon-32x32.png" rel="icon" sizes="32x32" type="image/png" />

        <link href="/favicon-16x16.png" rel="icon" sizes="16x16" type="image/png" />

        <link href="/favicon.ico" rel="shortcut icon" />

        <title>Doppel Vision</title>
      </Head>

      <Auth0Provider
        audience={process.env.NEXT_PUBLIC_AUTH0_AUDIENCE_HASURA}
        cacheLocation="memory"
        clientId={process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID}
        domain={process.env.NEXT_PUBLIC_AUTH0_DOMAIN}
        onRedirectCallback={(appState: AppState) => {
          // If there's an auth0 redirect path that's not the homepage, use it
          if (appState?.auth0RedirectPath && appState.auth0RedirectPath !== '/') {
            router.push(appState.auth0RedirectPath)
          } else {
            // Otherwise, check for default landing page preference
            const defaultLandingPage = Cookies.get('default_landing_page')
            if (
              defaultLandingPage &&
              AppSettingsDefaultLandingPageToRoute[defaultLandingPage]
            ) {
              router.push(AppSettingsDefaultLandingPageToRoute[defaultLandingPage])
            } else {
              // If no default landing page preference, go to homepage
              router.push('/')
            }
          }
        }}
        redirectUri={
          process.env.NEXT_PUBLIC_AUTH0_REDIRECT_URI ||
          (typeof document !== 'undefined' && document.location.origin)
        }
        useRefreshTokens
      >
        <PostHogProvider client={posthog}>
          <JotaiProvider>
            <IDTokenClaimsProvider>
              <AuthorizedApolloProvider>
                <InitializeAtoms />

                <ChakraProvider resetCSS theme={theme}>
                  <ErrorBoundary locationTag="_app.tsx">
                    <SlackNotificationUserOpenedHook />

                    <ReportsProvider>
                      <StatsContextProvider>
                        <ReportTableFilterProvider>
                          <SpoofReportTableFilterProvider>
                            <ToSWrapper>
                              <EntitiesTableProvider>
                                {layout(<Component {...pageProps} />)}
                              </EntitiesTableProvider>
                            </ToSWrapper>
                          </SpoofReportTableFilterProvider>
                        </ReportTableFilterProvider>
                      </StatsContextProvider>
                    </ReportsProvider>
                  </ErrorBoundary>
                </ChakraProvider>
              </AuthorizedApolloProvider>
            </IDTokenClaimsProvider>
          </JotaiProvider>
        </PostHogProvider>
      </Auth0Provider>
    </>
  )
}

export default MyApp
